import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Trash2, Truck } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, ToggleField } from "@/components/shared/form";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  useDeleteDriver,
  useDeleteTransporter,
  useDeleteVehicle,
  useDrivers,
  useTransporters,
  useUpsertDriver,
  useUpsertTransporter,
  useUpsertVehicle,
  useVehicles,
} from "@/features/fleet/api";
import {
  DRIVER_STATUSES,
  VEHICLE_STATUSES,
  VEHICLE_TYPES,
  driverStatusTone,
  labelFor,
  vehicleStatusTone,
} from "@/features/fleet/constants";
import {
  driverFormSchema,
  emptyDriverForm,
  emptyTransporterForm,
  emptyVehicleForm,
  transporterFormSchema,
  vehicleFormSchema,
  type DriverFormValues,
  type TransporterFormValues,
  type VehicleFormValues,
} from "@/features/fleet/schema";
import type { Driver, Transporter, Vehicle } from "@/features/fleet/types";
import { isSupabaseConfigured } from "@/lib/supabase";
import { cn } from "@/lib/utils";

type Tab = "transporters" | "vehicles" | "drivers";

export function FleetPage() {
  return (
    <ErrorBoundary title="Fleet page crashed">
      <FleetPageInner />
    </ErrorBoundary>
  );
}

function FleetPageInner() {
  const [tab, setTab] = useState<Tab>("transporters");
  const transportersQ = useTransporters();
  const vehiclesQ = useVehicles();
  const driversQ = useDrivers();

  const refetch = () => {
    void transportersQ.refetch();
    void vehiclesQ.refetch();
    void driversQ.refetch();
  };

  return (
    <MasterPage>
      <MasterHeader
        title="Fleet"
        icon={<Truck />}
        countLabel={`${transportersQ.data?.length ?? 0} transporters · ${vehiclesQ.data?.length ?? 0} vehicles · ${driversQ.data?.length ?? 0} drivers`}
        onRefresh={refetch}
        refreshing={transportersQ.isFetching || vehiclesQ.isFetching || driversQ.isFetching}
      />

      {!isSupabaseConfigured ? (
        <MasterBanner>Connect Supabase environment variables to enable live data.</MasterBanner>
      ) : null}

      <div className="flex gap-1 border-b border-border px-1">
        {(
          [
            ["transporters", "Transporters"],
            ["vehicles", "Vehicles"],
            ["drivers", "Drivers"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={cn(
              "px-3 py-1.5 text-xs font-medium",
              tab === key
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "transporters" ? (
        <TransportersPanel
          data={transportersQ.data ?? []}
          isLoading={transportersQ.isLoading}
          error={transportersQ.error as Error | null}
        />
      ) : null}
      {tab === "vehicles" ? (
        <VehiclesPanel
          data={vehiclesQ.data ?? []}
          transporters={transportersQ.data ?? []}
          drivers={driversQ.data ?? []}
          isLoading={vehiclesQ.isLoading}
          error={vehiclesQ.error as Error | null}
        />
      ) : null}
      {tab === "drivers" ? (
        <DriversPanel
          data={driversQ.data ?? []}
          transporters={transportersQ.data ?? []}
          isLoading={driversQ.isLoading}
          error={driversQ.error as Error | null}
        />
      ) : null}
    </MasterPage>
  );
}

function TransportersPanel({
  data,
  isLoading,
  error,
}: {
  data: Transporter[];
  isLoading: boolean;
  error: Error | null;
}) {
  const upsert = useUpsertTransporter();
  const remove = useDeleteTransporter();
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [editing, setEditing] = useState<Transporter | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Transporter | null>(null);

  const form = useForm<TransporterFormValues>({
    resolver: zodResolver(transporterFormSchema),
    defaultValues: emptyTransporterForm,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      if (activeOnly && !row.is_active) return false;
      if (!q) return true;
      return [row.code, row.name, row.phone, row.gstin, row.contact_person]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [data, search, activeOnly]);

  const openCreate = useCallback(() => {
    setEditing(null);
    form.reset(emptyTransporterForm);
    setOpen(true);
  }, [form]);

  useMasterListShortcuts({ searchInputId: "fleet-tp-search", onCreate: openCreate });

  function openEdit(row: Transporter) {
    setEditing(row);
    form.reset({
      code: row.code,
      name: row.name,
      gstin: row.gstin ?? "",
      contact_person: row.contact_person ?? "",
      phone: row.phone ?? "",
      email: row.email ?? "",
      address: row.address ?? "",
      payment_terms: row.payment_terms ?? "",
      notes: row.notes ?? "",
      is_active: row.is_active,
    });
    setOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  });

  return (
    <>
      {error ? <MasterBanner tone="danger">{error.message}</MasterBanner> : null}
      {!isLoading && data.length === 0 ? (
        <EmptyState
          title="No transporters"
          description="Add carriers used on dispatch notes."
          action={
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus />
              New transporter
            </Button>
          }
        />
      ) : (
        <>
          <MasterFilterBar shownLabel={`${filtered.length} shown`}>
            <MasterSearchInput
              id="fleet-tp-search"
              value={search}
              onChange={setSearch}
              placeholder="Search code or name…  (/)"
            />
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Active only
            </label>
            <Button type="button" size="sm" className="ml-auto" onClick={openCreate}>
              <Plus />
              New
            </Button>
          </MasterFilterBar>
          <MasterTable
            loading={isLoading}
            empty={!filtered.length}
            cols={["Code", "Name", "Contact", "Phone", "Status", ""]}
          >
            {filtered.map((row) => (
              <tr
                key={row.id}
                className="group cursor-pointer border-b border-border/70 hover:bg-muted/40"
                onClick={() => openEdit(row)}
              >
                <td className="px-2 py-1 font-mono text-xs font-medium">{row.code}</td>
                <td className="px-2 py-1 text-xs font-medium">{row.name}</td>
                <td className="px-2 py-1 text-xs text-muted-foreground">
                  {row.contact_person || "—"}
                </td>
                <td className="px-2 py-1 text-xs">{row.phone || "—"}</td>
                <td className="px-2 py-1">
                  <StatusDot tone={row.is_active ? "success" : "neutral"}>
                    {row.is_active ? "Active" : "Inactive"}
                  </StatusDot>
                </td>
                <td className="px-2 py-1 text-right" onClick={(e) => e.stopPropagation()}>
                  <RowActions onEdit={() => openEdit(row)} onDelete={() => setDeleting(row)} />
                </td>
              </tr>
            ))}
          </MasterTable>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit transporter" : "New transporter"}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <FieldGrid>
              <Field label="Code" required span={4} error={form.formState.errors.code?.message}>
                <Input className="uppercase" {...form.register("code")} />
              </Field>
              <Field label="Name" required span={8} error={form.formState.errors.name?.message}>
                <Input {...form.register("name")} />
              </Field>
              <Field label="GSTIN" span={6}>
                <Input {...form.register("gstin")} />
              </Field>
              <Field label="Contact" span={6}>
                <Input {...form.register("contact_person")} />
              </Field>
              <Field label="Phone" span={6}>
                <Input {...form.register("phone")} />
              </Field>
              <Field label="Email" span={6}>
                <Input {...form.register("email")} />
              </Field>
              <Field label="Payment terms" span={12}>
                <Input {...form.register("payment_terms")} />
              </Field>
              <Field label="Address" span={12}>
                <Input {...form.register("address")} />
              </Field>
              <Field label="Notes" span={12}>
                <Input {...form.register("notes")} />
              </Field>
              <ToggleField
                label="Active"
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v)}
                span={12}
              />
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete transporter?"
        description={deleting ? `${deleting.name} — prefer deactivate if used on dispatch.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

function VehiclesPanel({
  data,
  transporters,
  drivers,
  isLoading,
  error,
}: {
  data: Vehicle[];
  transporters: Transporter[];
  drivers: Driver[];
  isLoading: boolean;
  error: Error | null;
}) {
  const upsert = useUpsertVehicle();
  const remove = useDeleteVehicle();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"live" | "all">("live");
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Vehicle | null>(null);

  const form = useForm<VehicleFormValues>({
    resolver: zodResolver(vehicleFormSchema) as unknown as Resolver<VehicleFormValues>,
    defaultValues: emptyVehicleForm,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter === "live" && (row.status === "inactive" || !row.is_active)) return false;
      if (!q) return true;
      return [row.vehicle_number, row.transporter_name, row.driver_name, row.vehicle_type]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [data, search, statusFilter]);

  const openCreate = useCallback(() => {
    setEditing(null);
    form.reset(emptyVehicleForm);
    setOpen(true);
  }, [form]);

  useMasterListShortcuts({ searchInputId: "fleet-veh-search", onCreate: openCreate });

  function openEdit(row: Vehicle) {
    setEditing(row);
    form.reset({
      vehicle_number: row.vehicle_number,
      vehicle_type: row.vehicle_type,
      capacity_tons: row.capacity_tons ?? undefined,
      transporter_id: row.transporter_id ?? "",
      driver_id: row.driver_id ?? "",
      status: row.status,
      rate_per_km: row.rate_per_km ?? undefined,
      notes: row.notes ?? "",
      is_active: row.is_active,
    });
    setOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  });

  const activeTp = transporters.filter((t) => t.is_active);
  const activeDrivers = drivers.filter((d) => d.is_active && d.status !== "inactive");

  return (
    <>
      {error ? <MasterBanner tone="danger">{error.message}</MasterBanner> : null}
      {!isLoading && data.length === 0 ? (
        <EmptyState
          title="No vehicles"
          description="Register plant or hired vehicles for dispatch."
          action={
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus />
              New vehicle
            </Button>
          }
        />
      ) : (
        <>
          <MasterFilterBar shownLabel={`${filtered.length} shown`}>
            <MasterSearchInput
              id="fleet-veh-search"
              value={search}
              onChange={setSearch}
              placeholder="Search number…  (/)"
            />
            <NativeSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="w-28"
            >
              <option value="live">Active fleet</option>
              <option value="all">All</option>
            </NativeSelect>
            <Button type="button" size="sm" className="ml-auto" onClick={openCreate}>
              <Plus />
              New
            </Button>
          </MasterFilterBar>
          <MasterTable
            loading={isLoading}
            empty={!filtered.length}
            cols={["Number", "Type", "Transporter", "Driver", "Status", ""]}
          >
            {filtered.map((row) => (
              <tr
                key={row.id}
                className="group cursor-pointer border-b border-border/70 hover:bg-muted/40"
                onClick={() => openEdit(row)}
              >
                <td className="px-2 py-1 font-mono text-xs font-medium">{row.vehicle_number}</td>
                <td className="px-2 py-1">
                  <Badge variant="outline">{labelFor(VEHICLE_TYPES, row.vehicle_type)}</Badge>
                </td>
                <td className="px-2 py-1 text-xs text-muted-foreground">
                  {row.transporter_name || "—"}
                </td>
                <td className="px-2 py-1 text-xs">{row.driver_name || "—"}</td>
                <td className="px-2 py-1">
                  <StatusDot tone={vehicleStatusTone(row.status)}>
                    {labelFor(VEHICLE_STATUSES, row.status)}
                  </StatusDot>
                </td>
                <td className="px-2 py-1 text-right" onClick={(e) => e.stopPropagation()}>
                  <RowActions onEdit={() => openEdit(row)} onDelete={() => setDeleting(row)} />
                </td>
              </tr>
            ))}
          </MasterTable>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit vehicle" : "New vehicle"}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <FieldGrid>
              <Field
                label="Number"
                required
                span={6}
                error={form.formState.errors.vehicle_number?.message}
              >
                <Input className="uppercase" {...form.register("vehicle_number")} />
              </Field>
              <Field label="Type" span={6}>
                <NativeSelect {...form.register("vehicle_type")}>
                  {VEHICLE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Transporter" span={6}>
                <NativeSelect {...form.register("transporter_id")}>
                  <option value="">None</option>
                  {activeTp.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.code} — {t.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Default driver" span={6}>
                <NativeSelect {...form.register("driver_id")}>
                  <option value="">None</option>
                  {activeDrivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Capacity (T)" span={4}>
                <Input type="number" step="0.01" {...form.register("capacity_tons")} />
              </Field>
              <Field label="Rate / km" span={4}>
                <Input type="number" step="0.01" {...form.register("rate_per_km")} />
              </Field>
              <Field label="Status" span={4}>
                <NativeSelect {...form.register("status")}>
                  {VEHICLE_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Notes" span={12}>
                <Input {...form.register("notes")} />
              </Field>
              <ToggleField
                label="Active"
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v)}
                span={12}
              />
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete vehicle?"
        description={deleting ? `${deleting.vehicle_number}` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

function DriversPanel({
  data,
  transporters,
  isLoading,
  error,
}: {
  data: Driver[];
  transporters: Transporter[];
  isLoading: boolean;
  error: Error | null;
}) {
  const upsert = useUpsertDriver();
  const remove = useDeleteDriver();
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [editing, setEditing] = useState<Driver | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Driver | null>(null);

  const form = useForm<DriverFormValues>({
    resolver: zodResolver(driverFormSchema),
    defaultValues: emptyDriverForm,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      if (activeOnly && (!row.is_active || row.status === "inactive")) return false;
      if (!q) return true;
      return [row.name, row.phone, row.license_number, row.transporter_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [data, search, activeOnly]);

  const openCreate = useCallback(() => {
    setEditing(null);
    form.reset(emptyDriverForm);
    setOpen(true);
  }, [form]);

  useMasterListShortcuts({ searchInputId: "fleet-drv-search", onCreate: openCreate });

  function openEdit(row: Driver) {
    setEditing(row);
    form.reset({
      name: row.name,
      phone: row.phone ?? "",
      license_number: row.license_number ?? "",
      transporter_id: row.transporter_id ?? "",
      status: row.status,
      notes: row.notes ?? "",
      is_active: row.is_active,
    });
    setOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  });

  return (
    <>
      {error ? <MasterBanner tone="danger">{error.message}</MasterBanner> : null}
      {!isLoading && data.length === 0 ? (
        <EmptyState
          title="No drivers"
          description="Add drivers to assign on vehicles and dispatch."
          action={
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus />
              New driver
            </Button>
          }
        />
      ) : (
        <>
          <MasterFilterBar shownLabel={`${filtered.length} shown`}>
            <MasterSearchInput
              id="fleet-drv-search"
              value={search}
              onChange={setSearch}
              placeholder="Search name…  (/)"
            />
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Active only
            </label>
            <Button type="button" size="sm" className="ml-auto" onClick={openCreate}>
              <Plus />
              New
            </Button>
          </MasterFilterBar>
          <MasterTable
            loading={isLoading}
            empty={!filtered.length}
            cols={["Name", "Phone", "License", "Transporter", "Status", ""]}
          >
            {filtered.map((row) => (
              <tr
                key={row.id}
                className="group cursor-pointer border-b border-border/70 hover:bg-muted/40"
                onClick={() => openEdit(row)}
              >
                <td className="px-2 py-1 text-xs font-medium">{row.name}</td>
                <td className="px-2 py-1 text-xs">{row.phone || "—"}</td>
                <td className="px-2 py-1 font-mono text-2xs">{row.license_number || "—"}</td>
                <td className="px-2 py-1 text-xs text-muted-foreground">
                  {row.transporter_name || "—"}
                </td>
                <td className="px-2 py-1">
                  <StatusDot tone={driverStatusTone(row.status)}>
                    {labelFor(DRIVER_STATUSES, row.status)}
                  </StatusDot>
                </td>
                <td className="px-2 py-1 text-right" onClick={(e) => e.stopPropagation()}>
                  <RowActions onEdit={() => openEdit(row)} onDelete={() => setDeleting(row)} />
                </td>
              </tr>
            ))}
          </MasterTable>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit driver" : "New driver"}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <FieldGrid>
              <Field label="Name" required span={8} error={form.formState.errors.name?.message}>
                <Input {...form.register("name")} />
              </Field>
              <Field label="Status" span={4}>
                <NativeSelect {...form.register("status")}>
                  {DRIVER_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Phone" span={6}>
                <Input {...form.register("phone")} />
              </Field>
              <Field label="License" span={6}>
                <Input {...form.register("license_number")} />
              </Field>
              <Field label="Transporter" span={12}>
                <NativeSelect {...form.register("transporter_id")}>
                  <option value="">None</option>
                  {transporters
                    .filter((t) => t.is_active)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.code} — {t.name}
                      </option>
                    ))}
                </NativeSelect>
              </Field>
              <Field label="Notes" span={12}>
                <Input {...form.register("notes")} />
              </Field>
              <ToggleField
                label="Active"
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v)}
                span={12}
              />
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete driver?"
        description={deleting ? deleting.name : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

function MasterTable({
  cols,
  loading,
  empty,
  children,
}: {
  cols: string[];
  loading: boolean;
  empty: boolean;
  children: ReactNode;
}) {
  return (
    <div className="overflow-auto rounded-md border border-border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-2xs uppercase tracking-[0.05em] text-muted-foreground">
            {cols.map((c) => (
              <th key={c || "actions"} className="px-2 py-1.5 font-semibold">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={cols.length} className="px-2 py-8 text-center text-xs text-muted-foreground">
                Loading…
              </td>
            </tr>
          ) : empty ? (
            <tr>
              <td colSpan={cols.length} className="px-2 py-8 text-center text-xs text-muted-foreground">
                No matches
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <>
      <Button type="button" variant="ghost" size="icon-sm" aria-label="Edit" onClick={onEdit}>
        <Pencil />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Delete"
        className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
        onClick={onDelete}
      >
        <Trash2 />
      </Button>
    </>
  );
}
